import React, { createContext, useContext, useEffect, useState } from 'react';
import { WinCondition, CharacterConfig, ObjectConfig, SoundConfig, TextObjectConfig, SceneConfig, PhaserConfig } from '../types/config';

interface PhaserConfigContextType {
    characters: CharacterConfig[];
    objects: ObjectConfig[];
    texts: TextObjectConfig[];
    scene: SceneConfig;
    winConditions: WinCondition[];
    sounds: SoundConfig[];
    setCharactersForAll: (characters: CharacterConfig[]) => void;
    addCharacter: (character: CharacterConfig) => void;
    updateCharacter: (id: string, updates: Partial<CharacterConfig>) => void;
    removeCharacter: (id: string) => void;
    setObjectsForAll: (characters: ObjectConfig[]) => void;
    addObject: (object: ObjectConfig) => void;
    updateObject: (id: string, updates: Partial<ObjectConfig>) => void;
    removeObject: (id: string) => void;
    setTextForAll: (characters: TextObjectConfig[]) => void;
    addText: (text: TextObjectConfig) => void;
    updateText: (id: string, updates: Partial<TextObjectConfig>) => void;
    removeText: (id: string) => void;
    updateScene: (updates: Partial<SceneConfig>) => void;
    addWinCondition: (condition: WinCondition) => void;
    updateWinCondition: (id: string, updates: Partial<WinCondition>) => void;
    removeWinCondition: (id: string) => void;
    addSound: (sound: SoundConfig) => void;
    updateSound: (id: string, updates: Partial<SoundConfig>) => void;
    removeSound: (id: string) => void;
    reset: () => void;
}

const PhaserConfigContext = createContext<PhaserConfigContextType | null>(null);

export const usePhaserConfig = () => {
    const context = useContext(PhaserConfigContext);
    if (!context) {
        throw new Error('usePhaserConfig must be used within a PhaserConfigProvider');
    }
    return context;
};

export const PhaserConfigProvider: React.FC<{ children: React.ReactNode; initialConfig: PhaserConfig }> = ({ children, initialConfig }) => {
    const [characters, setCharacters] = useState<CharacterConfig[]>(initialConfig.characters || []);
    const [objects, setObjects] = useState<ObjectConfig[]>(initialConfig.objects || []);
    const [texts, setTexts] = useState<TextObjectConfig[]>(initialConfig.texts || []);
    const [winConditions, setWinConditions] = useState<WinCondition[]>(initialConfig.winConditions || []);
    const [scene, setScene] = useState<SceneConfig>(initialConfig.scene || {
        width: 400,
        height: 400,
        background: {
            image: '',
            mode: 'cover',
        },
    });
    const [sounds, setSounds] = useState<SoundConfig[]>(initialConfig.sounds || []);

// Sync internal state whenever the initialConfig prop changes
useEffect(() => {
    setCharacters(initialConfig.characters || []);
    setObjects(initialConfig.objects || []);
    setTexts(initialConfig.texts || []);
    setWinConditions(initialConfig.winConditions || []);
    setScene(
        initialConfig.scene || {
            width: 400,
            height: 400,
            background: {
                image: '',
                mode: 'cover',
            },
        }
    );
    setSounds(initialConfig.sounds || []);
}, [initialConfig]);
    // Méthodes pour les personnages
    const setCharactersForAll = (character: CharacterConfig[]) => {
        setCharacters(character);
    };

    const addCharacter = (character: CharacterConfig) => {
        setCharacters((prev) => [...prev, character]);
    };

    const updateCharacter = (id: string, updates: Partial<CharacterConfig>) => {
        setCharacters((prev) =>
            prev.map((char) => (char.id === id ? { ...char, ...updates } : char))
        );
    };

    const removeCharacter = (id: string) => {
        setCharacters((prev) => prev.filter((char) => char.id !== id));
    };

    // Méthodes pour les objets
    const setObjectsForAll = (object: ObjectConfig[]) => {
        setObjects(object);
    };

    const addObject = (object: ObjectConfig) => {
        setObjects((prev) => [...prev, object]);
    };

    const updateObject = (id: string, updates: Partial<ObjectConfig>) => {
        setObjects((prev) =>
            prev.map((obj) => (obj.id === id ? { ...obj, ...updates } : obj))
        );
    };

    const removeObject = (id: string) => {
        setObjects((prev) => prev.filter((obj) => obj.id !== id));
    };

    // Méthodes pour les textes
    const setTextForAll = (text: TextObjectConfig[]) => {
        setTexts(text);
    };

    const addText = (text: TextObjectConfig) => {
        setTexts((prev) => [...prev, text]);
    };

    const updateText = (id: string, updates: Partial<TextObjectConfig>) => {
        setTexts((prev) =>
            prev.map((txt) => (txt.id === id ? { ...txt, ...updates } : txt))
        );
    };

    const removeText = (id: string) => {
        setTexts((prev) => prev.filter((txt) => txt.id !== id));
    };

    // Méthode pour mettre à jour la scène
    const updateScene = (updates: Partial<SceneConfig>) => {
        setScene((prev) => ({
            ...prev,
            ...updates,
            background: {
                ...prev.background,
                ...updates.background,
            },
        }));
    };



    const addWinCondition = (condition: WinCondition) => {
        setWinConditions((prev) => [...prev, condition]);
    };

    const updateWinCondition = (id: string, updates: Partial<WinCondition>) => {
        setWinConditions((prev) =>
            prev.map((cond) => (cond.id === id ? { ...cond, ...updates } : cond))
        );
    };

    const removeWinCondition = (id: string) => {
        setWinConditions((prev) => prev.filter((cond) => cond.id !== id));
    };

    // Méthodes pour les sons
    const addSound = (sound: SoundConfig) => {
        setSounds((prev) => [...prev, sound]);
    };

    const updateSound = (id: string, updates: Partial<SoundConfig>) => {
        setSounds((prev) =>
            prev.map((snd) => (snd.id === id ? { ...snd, ...updates } : snd))
        );
    };

    const removeSound = (id: string) => {
        setSounds((prev) => prev.filter((snd) => snd.id !== id));
    };

    const reset = () => {
        setCharacters([])
        setObjects([])
        setWinConditions([])
        setTexts([])
    }

    return (
        <PhaserConfigContext.Provider
            value={{
                characters,
                objects,
                texts,
                scene,
                winConditions,
                sounds,
                setCharactersForAll,
                addCharacter,
                updateCharacter,
                removeCharacter,
                setObjectsForAll,
                addObject,
                updateObject,
                removeObject,
                setTextForAll,
                addText,
                updateText,
                removeText,
                updateScene,
                addWinCondition,
                updateWinCondition,
                removeWinCondition,
                addSound,
                updateSound,
                removeSound,
                reset,
            }}
        >
            {children}
        </PhaserConfigContext.Provider>
    );
};
